/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
'use strict'

import { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col, ProgressBar, Alert } from 'react-bootstrap';
import { useContext } from 'react';
import { MetadataContext, MetadataContextType } from './MetadataContext';
import FileList from './FileList';
import { getMetadata } from '../data';
import { WsContext, WsContextType } from './WsContext';
import { formatBytes } from '../utils/formatBytes';


interface SaveDialogProps {
    cloud: boolean;
    onSave: (filename: string, cloud : boolean, metadataOverride?: { info: string; serial: string }, progressId?: string) => Promise<void>;
    onCancel: () => void;
    isOpen: boolean;
    title?: string;
    primaryLabel?: string;
    defaultFilename?: string;
    filenameTemplate?: string;
}

const PRIVATE_DIR_KEY = '__private__';
const NON_PRIVATE_DIRS = ['public', 'library/dev', 'library/queue', 'library/reject', 'library/merged'];

function SaveDialog({ onSave, onCancel, isOpen, title, primaryLabel, defaultFilename, filenameTemplate } : SaveDialogProps) {
    const metadataContext = useContext<MetadataContextType>(MetadataContext);
    const wsContext = useContext<WsContextType>(WsContext);
    const isPlaceholder = (value?: string) => {
        if (!value) {
            return true;
        }
        const normalized = value.trim().toLowerCase();
        return normalized === '' || normalized === 'unknown' || normalized === 'loading';
    };
    const resolvedUserDir = metadataContext.user_dir;
    const privateDirectory = !isPlaceholder(resolvedUserDir)
        ? resolvedUserDir
        : 'user';
    const resolveDirectory = (choice: string) => (choice === PRIVATE_DIR_KEY ? privateDirectory : choice);
    const normalizeDirectoryChoice = (savedDir: string) => {
        const trimmed = (savedDir || '').trim();
        if (trimmed === PRIVATE_DIR_KEY) {
            return PRIVATE_DIR_KEY;
        }
        if (NON_PRIVATE_DIRS.includes(trimmed)) {
            return trimmed;
        }
        if (isPlaceholder(trimmed) || trimmed.toLowerCase() === 'user' || trimmed === privateDirectory) {
            return PRIVATE_DIR_KEY;
        }
        return PRIVATE_DIR_KEY;
    };
    const defaultDirectoryChoice = normalizeDirectoryChoice(metadataContext.prefix || '');
    const defaultName = defaultFilename || `${privateDirectory}_${new Date().toISOString().slice(0,10).replace(/-/g, '')}.fbot`;
    const [filename, setFilename] = useState(defaultName);
    const [filenameTouched, setFilenameTouched] = useState(false);
    const [ cloud, setCloud ] = useState<boolean>(true);
    const [ directoryChoice, setDirectoryChoice ] = useState<string>(defaultDirectoryChoice);
    const [ search, setSearch ] = useState<string>('');
    const [ metadataSerial, setMetadataSerial ] = useState<string>('');
    const [ metadataInfo, setMetadataInfo ] = useState<string>('');
    const [activeProgressId, setActiveProgressId] = useState<string | null>(null);
    const [progressNow, setProgressNow] = useState<number>(0);
    const [progressLabel, setProgressLabel] = useState<string>('');
    const [bytesTransferred, setBytesTransferred] = useState<number>(0);
    const [totalBytes, setTotalBytes] = useState<number | null>(null);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const directory = resolveDirectory(directoryChoice);

    const makeProgressId = () => {
        return crypto.randomUUID();
    };

    const resolveTemplateName = (serialValue: string) => {
        if (!filenameTemplate) {
            return defaultName;
        }
        const safeSerial = serialValue || 'unknown';
        return filenameTemplate.replace('{serial}', safeSerial);
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        try {
            const savedCloud = localStorage.getItem('fbot.save.cloud');
            if (savedCloud !== null) {
                setCloud(savedCloud === 'true');
            }
            const savedDir = localStorage.getItem('fbot.save.dir');
            if (savedDir) {
                setDirectoryChoice(normalizeDirectoryChoice(savedDir));
            } else {
                setDirectoryChoice(defaultDirectoryChoice);
            }
        } catch (e) {
            // ignore localStorage errors
        }
        setFilenameTouched(false);
        setFilename(resolveTemplateName(''));
        setActiveProgressId(null);
        setProgressNow(0);
        setProgressLabel('');
        setBytesTransferred(0);
        setTotalBytes(null);
        setIsSaving(false);
        setSaveError(null);
        async function loadMetadata() {
            try {
                const meta = await getMetadata();
                setMetadataSerial(meta.serial || '');
                setMetadataInfo(meta.info || '');
            } catch (e) {
                setMetadataSerial('');
                setMetadataInfo('');
            }
        }
        loadMetadata();
    }, [isOpen, defaultDirectoryChoice]);

    useEffect(() => {
        try {
            localStorage.setItem('fbot.save.cloud', cloud ? 'true' : 'false');
        } catch (e) {
            // ignore localStorage errors
        }
    }, [cloud]);

    useEffect(() => {
        try {
            localStorage.setItem('fbot.save.dir', directoryChoice);
        } catch (e) {
            // ignore localStorage errors
        }
    }, [directoryChoice]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        if (!filenameTouched) {
            setFilename(resolveTemplateName(metadataSerial));
        }
    }, [defaultName, filenameTouched, isOpen, metadataSerial]);

    useEffect(() => {
        if (!wsContext.ws || !isOpen) {
            return;
        }
        const handler = (msg: any) => {
            if (!activeProgressId || msg?.progressId !== activeProgressId || msg?.op !== 'save') {
                return;
            }
            if (typeof msg?.now === 'number') {
                setProgressNow(Math.max(0, Math.min(100, msg.now)));
            }
            if (typeof msg?.bytesTransferred === 'number') {
                setBytesTransferred(Math.max(0, msg.bytesTransferred));
            }
            if (typeof msg?.totalBytes === 'number' && msg.totalBytes > 0) {
                setTotalBytes(msg.totalBytes);
            }
            setProgressLabel(msg?.label || 'Transferring...');
        };
        wsContext.ws.on('fileTransferProgress', handler);
        return () => {
            wsContext.ws.off('fileTransferProgress', handler);
        };
    }, [wsContext.ws, isOpen, activeProgressId]);

    if (!isOpen) return null;

    const handleSave = async () => {
        if (filename.trim()) {
            const progressId = makeProgressId();
            setIsSaving(true);
            setSaveError(null);
            setActiveProgressId(progressId);
            setProgressNow(0);
            setProgressLabel(cloud ? 'Preparing upload...' : 'Processing...');
            setBytesTransferred(0);
            setTotalBytes(null);
            const trimmed = filename.trim();
            const override = { info: metadataInfo, serial: metadataSerial };
            try {
                if (trimmed.includes('/')) {
                    await onSave(trimmed, cloud, override, progressId);
                    onCancel();
                    return;
                }
                const key = `${directory}/${trimmed}`;
                await onSave(key, cloud, override, progressId);
                onCancel();
            } catch (err: any) {
                setSaveError(err?.message || 'Save failed');
            } finally {
                setIsSaving(false);
            }
        }
    };

    return (
        <Modal show={isOpen} onHide={onCancel} centered dialogClassName="modal-xx1">
            <Modal.Header closeButton>
            <Modal.Title>{title || 'Save'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
            <div className="mb-3">
                <Row className="g-2 align-items-end">
                    <Col xs={12} md="auto">
                        <Form.Label>Origin</Form.Label>
                        <div className="btn-group w-100" role="group">
                            <Button
                                variant={cloud ? "primary" : "outline-primary"}
                                onClick={() => setCloud(true)}
                            >
                                Cloud
                            </Button>
                            <Button
                                variant={!cloud ? "primary" : "outline-primary"}
                                onClick={() => setCloud(false)}
                            >
                                Local
                            </Button>
                        </div>
                    </Col>
                    <Col xs={12} md>
                        <Form.Group controlId="directory">
                            <Form.Label>Directory</Form.Label>
                            <Form.Select
                                value={directoryChoice}
                                onChange={(e) => setDirectoryChoice(e.target.value)}
                                className="form-select"
                            >
                                <option value={PRIVATE_DIR_KEY}>{`Private (${privateDirectory})`}</option>
                                <option value="public">Public</option>
                                <option value="library/dev">Dev Library</option>
                                <option value="library/queue">Submission Queue</option>
                                <option value="library/reject">Rejected Submissions</option>
                                <option value="library/merged">Merged Submissions</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md>
                        <Form.Group controlId="search">
                            <Form.Label>Search</Form.Label>
                            <Form.Control
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search files..."
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </div>

            <Form.Group controlId="filename">
            <Form.Label>Filename:</Form.Label>
            <Form.Control
            type="text"
            value={filename}
            onChange={(e) => {
                setFilenameTouched(true);
                setFilename(e.target.value);
            }}
            placeholder="Enter filename"
            />
            </Form.Group>
            <Row className="mt-3">
                <Col md={4}>
                    <Form.Group controlId="metadataSerial">
                        <Form.Label>Metadata Serial</Form.Label>
                        <Form.Control
                            type="text"
                            value={metadataSerial}
                            onChange={(e) => setMetadataSerial(e.target.value)}
                            placeholder="Serial"
                        />
                    </Form.Group>
                </Col>
                <Col md={8}>
                    <Form.Group controlId="metadataNotes">
                        <Form.Label>Metadata Notes</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={metadataInfo}
                            onChange={(e) => setMetadataInfo(e.target.value)}
                            placeholder="Notes"
                        />
                    </Form.Group>
                </Col>
            </Row>
            <div className="mt-3">
                <FileList cloud={cloud} prefix={directory} allowDelete={true} filter={/\.fbot$/} showSearch={false} search={search} onSearchChange={setSearch}
                    clickFile={(fn : string) => {
                        setFilenameTouched(true);
                        setFilename(fn);
                    }}
                />
            </div>
            {saveError && (
                <Alert variant="danger" className="mt-3 mb-0">
                    {saveError}
                </Alert>
            )}
            </Modal.Body>
            <Modal.Footer>
                {activeProgressId && cloud && (
                    <div className="w-100 mb-2">
                        <div className="small mb-1">{progressLabel || 'Uploading...'}</div>
                        <div className="small text-muted mb-1">
                            {formatBytes(bytesTransferred)} / {totalBytes ? formatBytes(totalBytes) : 'unknown'}
                        </div>
                        <ProgressBar now={progressNow} label={`${Math.round(progressNow)}%`} animated={progressNow > 0 && progressNow < 100} />
                    </div>
                )}
                <Button variant="secondary" onClick={onCancel}>Cancel</Button>
                <Button variant="primary" onClick={handleSave} disabled={isSaving}>{isSaving ? 'Saving...' : (primaryLabel || 'Save')}</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SaveDialog;